import {
  HubEntitlementState,
  type HubFeature,
  type HubFlagEntitlementStatus,
  type HubLimitEntitlementStatus,
} from '@robovision/hub'


export enum EntitlementKind {
  FLAG = 'flag',
  LIMIT = 'limit',
}

export interface EntitlementParams {
  expiresAt?: Date
  feature: HubFeature
  state: HubEntitlementState
}

export interface LimitEntitlementParams extends EntitlementParams {
  limit?: number
  used?: number
}

export abstract class Entitlement {
  public abstract readonly kind: EntitlementKind
  public expiresAt?: Date
  public feature: HubFeature
  public state: HubEntitlementState

  constructor (params: EntitlementParams) {
    this.expiresAt = params.expiresAt
    this.feature = params.feature
    this.state = params.state
  }

  /** Whether the grant is in force, so the feature may be used right now. */
  public get isGranted (): boolean {
    return this.state === HubEntitlementState.Granted
  }

  public get isLapsed (): boolean {
    return this.state === HubEntitlementState.Lapsed
  }

  /** Whether the license carries the feature at all, in force or not. */
  public get isOnLicense (): boolean {
    return this.state !== HubEntitlementState.NotGranted
  }

  public static fromResponse (data: HubFlagEntitlementStatus | HubLimitEntitlementStatus): AnyEntitlement {
    if (data.kind === EntitlementKind.LIMIT) {
      return LimitEntitlement.fromResponse(data)
    }
    return FlagEntitlement.fromResponse(data)
  }
}

export class FlagEntitlement extends Entitlement {
  public readonly kind = EntitlementKind.FLAG

  public static override fromResponse (data: HubFlagEntitlementStatus): FlagEntitlement {
    return new FlagEntitlement({
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      feature: data.feature,
      state: data.state,
    })
  }
}

export class LimitEntitlement extends Entitlement {
  public readonly kind = EntitlementKind.LIMIT
  public limit?: number
  public used?: number

  constructor (params: LimitEntitlementParams) {
    super(params)
    this.limit = params.limit
    this.used = params.used
  }

  public get usage (): string | undefined {
    if (this.used === undefined || this.limit === undefined) return undefined

    return `${this.used}/${this.limit}`
  }

  /** Whether usage is at or over the limit */
  public get isLimitReached (): boolean {
    if (this.used === undefined || this.limit === undefined) return false

    return this.used >= this.limit
  }

  /** Whether usage is strictly over the limit */
  public get isLimitExceeded (): boolean {
    if (this.used === undefined || this.limit === undefined) return false

    return this.used > this.limit
  }

  public static override fromResponse (data: HubLimitEntitlementStatus): LimitEntitlement {
    return new LimitEntitlement({
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      feature: data.feature,
      limit: data.limit,
      state: data.state,
      used: data.used,
    })
  }
}

export type AnyEntitlement = FlagEntitlement | LimitEntitlement
